'use strict'

const get_ = require('lodash/get')
const { default: Experiments } = require('@wix/wix-experiments')
const {
  serializeMessage
} = require('@wix/santa-core-utils/dist/cjs/coreUtils/core/logWixCodeConsoleMessage')
const {
  fetchUserCode: _fetchUserCode,
  fetchUserCodeAsync: _fetchUserCodeAsync,
  startUserCode: _startUserCode
} = require('./userCodeHandler')
const workerLogger = require('./workerLogger')
const { isWebWorker } = require('./isWebWorker')
const { importSync, importAsync } = require('./importScriptAsAmdModule')
const elementoryArgumentsHandler = require('./elementoryArgumentsHandler')
const { createLegacy } = require('./legacyApp')
const traceCreators = require('./logger/traceCreators')
const biEventCreators = require('./logger/biEventCreators')

const EXPERIMENTS_HOST = 'https://www.wix.com'
const EXPERIMENTS_SCOPE = 'wix-code'

const createNew = ({ appLogger, userConsole }) => {
  const userCodeModules = new Map()
  let storage

  let onLog = () => {
    throw new Error('onLog was used before it was created')
  }

  let experimentsManager = null

  const loadUserCode = async ({ userCodeMap, isWebWorker }) => {
    isWebWorker
      ? await _fetchUserCode(
          userConsole,
          appLogger,
          userCodeModules,
          userCodeMap,
          importSync
        )
      : await _fetchUserCodeAsync(
          appLogger,
          userCodeModules,
          userCodeMap,
          importAsync
        )

    appLogger.bi(biEventCreators.userCodeLoaded({ pageId: userCodeMap[0].id }))
  }

  const initApp = async ({ wixCodeApi, userCodeMap, isWebWorker }) => {
    elementoryArgumentsHandler.setExtraHeaders(wixCodeApi, appLogger)

    experimentsManager = new Experiments({
      scope: EXPERIMENTS_SCOPE,
      baseUrl: EXPERIMENTS_HOST
    })

    await appLogger.traceAsync(
      traceCreators.loadUserCode(),
      traceCreators.errorToTraceOptions,
      () => loadUserCode({ userCodeMap, isWebWorker })
    )

    onLog = workerLogger.wrapConsole(userConsole)
    const viewMode = get_(wixCodeApi, ['window', 'viewMode'])
    if (viewMode === 'Preview') {
      onLog(consoleMessage => {
        wixCodeApi.site.notifyEventToEditorApp('wix-code', {
          eventType: 'addConsoleMessage',
          eventPayload: {
            consoleMessage: serializeMessage(consoleMessage)
          }
        })
      })
    }
  }

  const initAppLogger = ({
    wixCodeApi,
    reportTrace,
    biLoggerFactory,
    createRavenClient,
    userCodeMap,
    isWebWorker
  }) => {
    const userId = get_(wixCodeApi, ['user', 'currentUser', 'id'])

    appLogger.init({
      user: {
        id: userId
      },
      hostType: isWebWorker ? 'worker' : 'iframe',
      reportTrace,
      biLoggerFactory,
      createRavenClient
    })

    appLogger.addSessionData(() => ({
      userCodeScripts: userCodeMap,
      elementoryArguments: {
        baseUrl: self.elementorySupport.baseUrl,
        queryParameters: self.elementorySupport.queryParameters,
        options: self.elementorySupport.options
      }
    }))
  }

  const initAppForPage = async (
    applicationData,
    platformUtils,
    wixCodeApi,
    additionalParams
  ) => {
    try {
      const {
        // instance,
        // instanceId,
        // url,
        appData: { userCodeMap }
      } = applicationData
      const {
        biLoggerFactory,
        // fedOpsLoggerFactory,
        // getCsrfToken,
        monitoring,
        reportTrace
      } = additionalParams
      storage = platformUtils.storage

      const _isWebWorker = isWebWorker()
      initAppLogger({
        wixCodeApi,
        reportTrace,
        biLoggerFactory,
        createRavenClient: monitoring.createMonitor,
        userCodeMap,
        isWebWorker: _isWebWorker
      })

      await appLogger.traceAsync(
        traceCreators.initAppForPage(),
        traceCreators.errorToTraceOptions,
        () => initApp({ wixCodeApi, userCodeMap, isWebWorker: _isWebWorker })
      )
    } catch (e) {
      appLogger.error(e)
      throw e
    }
  }

  const _createControllers = rawControllerConfigs => {
    const [controllerConfig] = rawControllerConfigs
    const {
      $w,
      wixCodeApi,
      appParams: {
        instance,
        appData: { userCodeMap }
      }
    } = controllerConfig

    const wixCodeScripts = userCodeMap.map(script => {
      return { script, $w }
    })

    const userExports = _startUserCode({
      userConsole,
      appLogger,
      experimentsManager,
      instance,
      wixSdk: wixCodeApi,
      storage,
      userCodeModules,
      wixCodeScripts,
      onLog
    })

    wixCodeApi.events.setStaticEventHandlers(userExports)

    return []
  }

  const createControllers = rawControllerConfigs => {
    try {
      const traceId = appLogger.traceStart(traceCreators.createControllers())
      const result = _createControllers(rawControllerConfigs)
      appLogger.traceEnd({ traceId })

      return result
    } catch (e) {
      appLogger.error(e)
      throw e
    }
  }

  return {
    initAppForPage,
    createControllers
  }
}

const create = ({ appLogger, userConsole }) => {
  const {
    fetchUserCode,
    fetchUserCodeAsync,
    startUserCode,
    wixCodeBootstrap,
    updateWixCodeData
  } = createLegacy({ appLogger, userConsole })

  const { initAppForPage, createControllers } = createNew({
    appLogger,
    userConsole
  })

  return {
    initAppForPage,
    createControllers,
    fetchUserCode,
    fetchUserCodeAsync,
    startUserCode,
    wixCodeBootstrap,
    updateWixCodeData
  }
}

module.exports.create = create
